import { useQuery } from '@tanstack/react-query';
import {
  validateInvitationToken,
  isInvitationTokenErrorCode,
  type InvitationTokenErrorCode,
  type InvitationTokenExpiredDetails,
  type InvitationTokenRevokedDetails,
} from '../../infrastructure/api/invitation.api.repository';

export interface InvitationError {
  kind: 'token';
  code: InvitationTokenErrorCode;
  message?: string;
  expiredDetails?: InvitationTokenExpiredDetails;
  revokedDetails?: InvitationTokenRevokedDetails;
}

export interface InvitationNetworkError {
  kind: 'network';
  message: string;
}

export type InvitationQueryError = InvitationError | InvitationNetworkError;

export function useInvitation(token: string | null) {
  const query = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => validateInvitationToken(token!),
    enabled: !!token && token.length > 0,
    refetchOnWindowFocus: false,
  });

  const error: InvitationQueryError | null =
    query.error != null
      ? (() => {
          const err = query.error as Error & {
            code?: unknown;
            expiredDetails?: InvitationTokenExpiredDetails;
            revokedDetails?: InvitationTokenRevokedDetails;
          };
          if (isInvitationTokenErrorCode(err.code)) {
            return {
              kind: 'token',
              code: err.code,
              message: err.message,
              expiredDetails: err.expiredDetails,
              revokedDetails: err.revokedDetails,
            };
          }
          return {
            kind: 'network',
            message:
              err.message ||
              'Impossible de contacter le serveur. Vérifiez votre connexion.',
          };
        })()
      : null;

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error,
    refetch: query.refetch,
  };
}
